import type { ProposalStatus, ProviderProposal } from '@hemmafixa/shared';
import {
  Button,
  DataTable,
  EmptyState,
  FilterChips,
  MasterDetail,
  PageHeader,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  type ColumnDef,
} from '@hemmafixa/ui';
import { HandCoins, MousePointerClick } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import { useCategoryLabel } from '../shared/catalogue';
import { DetailPanel, FullPageLink, SelectableCard } from '../shared/master-detail-parts';
import { QueryView } from '../shared/query-view';
import { useStatuses } from '../shared/statuses';
import { useSelectedParam, useShownSelection } from '../shared/use-selected-param';
import { useMyProposals } from './api';
import { ProviderJobDetail } from './provider-job-page';

type Filter = 'all' | 'active' | 'closed';
const PAGE_SIZE = 10;
const formatPrice = (proposal: ProviderProposal, money: (ore: number) => string) =>
  proposal.priceToOre
    ? `${money(proposal.priceFromOre)} – ${money(proposal.priceToOre)}`
    : money(proposal.priceFromOre);

const STATUS_FILTERS: ProposalStatus[] = [
  'submitted',
  'accepted',
  'chosen',
  'declined',
  'withdrawn',
  'closed',
];

/** Every proposal the provider has sent, newest first, by status group or one status (P4-06). */
export function MyProposalsPage() {
  const { t, money, date } = useI18n();
  const statuses = useStatuses();
  const categoryLabel = useCategoryLabel();
  const [picked, setSelected] = useSelectedParam();
  const [filter, setFilter] = useState<Filter>('all');
  const [status, setStatus] = useState<ProposalStatus | 'any'>('any');
  const [page, setPage] = useState(1);
  const p = t.provider.proposals;
  const query = useMyProposals({
    filter,
    status: status === 'any' ? undefined : status,
    page,
    pageSize: PAGE_SIZE,
  });
  const selected = useShownSelection(
    picked,
    query.data?.items.map((item) => item.job.id),
  );

  const price = (proposal: ProviderProposal) => formatPrice(proposal, money);

  const columns = useMemo<ColumnDef<ProviderProposal, unknown>[]>(
    () => [
      {
        id: 'job',
        header: p.columns.job,
        cell: ({ row }) => (
          <span className="grid">
            <span className="font-medium">{row.original.job.title}</span>
            <span className="text-xs text-muted-foreground">
              {categoryLabel(row.original.job.categorySlug)}
            </span>
          </span>
        ),
      },
      {
        id: 'price',
        header: p.columns.price,
        cell: ({ row }) => <span className="tabular-nums">{formatPrice(row.original, money)}</span>,
      },
      {
        accessorKey: 'status',
        header: p.columns.status,
        cell: ({ row }) => <StatusBadge status={row.original.status} statuses={statuses.proposal} />,
      },
      { accessorKey: 'createdAt', header: p.columns.sent, cell: ({ row }) => date(row.original.createdAt) },
    ],
    [p, money, date, statuses, categoryLabel],
  );

  // Choosing a group clears the single status, and the other way round, so they never contradict
  const statusOptions = STATUS_FILTERS.filter(
    (value) =>
      filter === 'all' ||
      (filter === 'active'
        ? ['submitted', 'accepted'].includes(value)
        : !['submitted', 'accepted'].includes(value)),
  );

  return (
    <div className="grid gap-4">
      <PageHeader title={p.title} description={p.description} />
      {/* The group as chips, the single status as a compact menu next to them */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterChips
          label={p.show}
          value={filter}
          onChange={(value) => {
            setFilter(value);
            setStatus('any');
            setPage(1);
          }}
          options={(['all', 'active', 'closed'] as const).map((value) => ({
            value,
            label: p.filters[value],
          }))}
        />
        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value as ProposalStatus | 'any');
            setPage(1);
          }}
        >
          <SelectTrigger
            aria-label={p.columns.status}
            className="h-8 w-36 rounded-full border-transparent bg-surface text-xs shadow-sm"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{t.common.all}</SelectItem>
            {statusOptions.map((value) => (
              <SelectItem key={value} value={value}>
                {t.status.proposal[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Beside the filters, so the details start level with the list */}
        {selected && (
          <FullPageLink
            to={`/provider/jobs/${selected}`}
            className="ml-auto hidden shrink-0 lg:inline-flex"
          />
        )}
      </div>
      <MasterDetail
        open={selected !== null}
        onClose={() => setSelected(null)}
        labels={{ details: p.title }}
        placeholder={<EmptyState icon={MousePointerClick} title={t.common.pickItem} />}
        detail={
          selected ? (
            <DetailPanel fullPage={`/provider/jobs/${selected}`} linkBesideTabs>
              <ProviderJobDetail id={selected} />
            </DetailPanel>
          ) : null
        }
        list={
          <>
            <QueryView query={query}>
              {(result) => (
                <>
                  <DataTable
                    layout="cards"
                    columns={columns}
                    data={result.items}
                    getRowId={(proposal) => proposal.id}
                    renderCard={(proposal) => (
                      <SelectableCard
                        to={`/provider/jobs/${proposal.job.id}`}
                        selected={proposal.job.id === selected}
                        onSelect={() => setSelected(proposal.job.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <p className="font-semibold">{proposal.job.title}</p>
                          <StatusBadge status={proposal.status} statuses={statuses.proposal} />
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {categoryLabel(proposal.job.categorySlug)} ·{' '}
                          <span className="tabular-nums">{price(proposal)}</span> · {date(proposal.createdAt)}
                        </p>
                      </SelectableCard>
                    )}
                    empty={
                      <EmptyState
                        icon={HandCoins}
                        title={p.empty}
                        description={p.emptyText}
                        action={
                          <Button asChild variant="outline">
                            <Link to="/provider/jobs">{p.findJobs}</Link>
                          </Button>
                        }
                      />
                    }
                  />
                  <Pagination
                    page={result.page}
                    pageSize={result.pageSize}
                    total={result.total}
                    onPageChange={setPage}
                    labels={{
                      previous: t.common.previous,
                      next: t.common.next,
                      showing: (from, to, total) => fill(t.common.showingRange, { from, to, total }),
                    }}
                  />
                </>
              )}
            </QueryView>
          </>
        }
      />
    </div>
  );
}
